import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { outerSize, type Box, type Vec3 } from './box';
import { boxerDict } from './i18n';

/** The drawing's frame in px; every box is scaled to fit it. */
const FRAME_W = 320;
const FRAME_H = 240;
/** Room around the box for the dimension labels. */
const PAD = 36;
const COS = Math.cos(Math.PI / 6);
const SIN = Math.sin(Math.PI / 6);

/** Isometric: x runs down-right, y down-left, z straight up. */
const project = ([x, y, z]: Vec3): [number, number] => [
  (x - y) * COS,
  (x + y) * SIN - z,
];

/**
 * The box as it will print, in true proportions: the far inner walls and the
 * floor seen through the open top, the two near outer walls over them, and
 * the rim — the wall thickness — drawn last. Only the outer size is labelled;
 * the page states the inner size in text.
 */
export function BoxPreview({ box }: { box: Box }) {
  const t = useDict(boxerDict);
  const { locale } = useLocale();
  const num = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const { x: L, y: W, z: H } = outerSize(box);
  const w = box.wall;

  const corners: Vec3[] = [
    [0, 0, 0],
    [L, 0, 0],
    [L, W, 0],
    [0, W, 0],
    [0, 0, H],
    [L, 0, H],
    [L, W, H],
    [0, W, H],
  ];
  const flat = corners.map(project);
  const minX = Math.min(...flat.map(([x]) => x));
  const maxX = Math.max(...flat.map(([x]) => x));
  const minY = Math.min(...flat.map(([, y]) => y));
  const maxY = Math.max(...flat.map(([, y]) => y));
  const scale = Math.min(
    (FRAME_W - 2 * PAD) / (maxX - minX),
    (FRAME_H - 2 * PAD) / (maxY - minY),
  );
  // Centred in the frame whatever the proportions.
  const offX = (FRAME_W - (maxX - minX) * scale) / 2 - minX * scale;
  const offY = (FRAME_H - (maxY - minY) * scale) / 2 - minY * scale;
  const at = (v: Vec3): [number, number] => {
    const [x, y] = project(v);
    return [x * scale + offX, y * scale + offY];
  };
  const pt = (v: Vec3) =>
    at(v)
      .map((n) => n.toFixed(1))
      .join(',');
  const poly = (...vs: Vec3[]) => vs.map(pt).join(' ');
  const rect = (x0: number, y0: number, x1: number, y1: number, z: number) =>
    [
      [x0, y0, z],
      [x1, y0, z],
      [x1, y1, z],
      [x0, y1, z],
    ] as Vec3[];
  const ring = (vs: Vec3[]) => `M${vs.map(pt).join('L')}Z`;

  const label = (v: Vec3, dx: number, dy: number, text: string) => {
    const [x, y] = at(v);
    return (
      <text
        x={x + dx}
        y={y + dy}
        textAnchor={dx < 0 ? 'end' : dx > 0 ? 'start' : 'middle'}
        className="box-preview-label"
      >
        {text}
      </text>
    );
  };

  return (
    <svg
      role="img"
      aria-label={t.previewLabel(
        num.format(box.length),
        num.format(box.width),
        num.format(box.height),
        num.format(box.wall),
      )}
      viewBox={`0 0 ${FRAME_W} ${FRAME_H}`}
      className="box-preview"
    >
      <polygon
        points={poly(...rect(w, w, L - w, W - w, w))}
        className="box-preview-floor"
      />
      <polygon
        points={poly([w, w, w], [w, W - w, w], [w, W - w, H], [w, w, H])}
        className="box-preview-inner"
      />
      <polygon
        points={poly([w, w, w], [L - w, w, w], [L - w, w, H], [w, w, H])}
        className="box-preview-inner"
      />
      <polygon
        points={poly([0, W, 0], [L, W, 0], [L, W, H], [0, W, H])}
        className="box-preview-side-left"
      />
      <polygon
        points={poly([L, 0, 0], [L, W, 0], [L, W, H], [L, 0, H])}
        className="box-preview-side-right"
      />
      <path
        d={`${ring(rect(0, 0, L, W, H))}${ring(rect(w, w, L - w, W - w, H))}`}
        fillRule="evenodd"
        className="box-preview-rim"
      />
      {label([L / 2, W, 0], -6, 16, `${t.labelL} ${num.format(box.length)}`)}
      {label([L, W / 2, 0], 6, 16, `${t.labelW} ${num.format(box.width)}`)}
      {label([0, W, H / 2], -8, 4, `${t.labelH} ${num.format(box.height)}`)}
    </svg>
  );
}
